/**
 * Subscription Store — 결제 재작업 Phase 3
 *
 * 학원(academy) 좌석권 구독 상태를 관리 (ISSUE_182: 학원 단일, 연습장 폐기).
 *  - academy: 좌석권(월/연). 프리미엄 자격 = active + past_due(7일 유예 중) → 세션 다기기 동기화·
 *    realtime 협업·다교사 좌석. 미결제(trialing)·만료(canceled)여도 마스터데이터(학생·수업 등)는
 *    무료로 서버 저장됨 — 프리미엄 기능만 잠김. 자격 판정은 isPremiumActive() 단일 소스.
 *  - free/sync(개인 플랜)는 폐기 — personalSubscription 제거.
 *
 * @module core/store/useSubscriptionStore
 */

import { create } from "zustand";

// ============================================================================
// TYPES
// ============================================================================

/** DB subscription_plan enum 미러. 신모델은 academy 만 실사용(free/sync 폐기). */
export type SubscriptionPlan = "free" | "sync" | "academy";
export type SubscriptionStatus = "active" | "past_due" | "canceled" | "trialing";
export type BillingCycle = "monthly" | "annual";

export interface Subscription {
  id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  /** 과금 단위 = active 좌석 수 (subscriptions.seat_count 캐시). */
  activeSeatCount: number;
  /** 멈춤 좌석 수 (다음 주기 0원). */
  pausedSeatCount: number;
  pricePerSeat: number | null;
  trialEndsAt: string | null;
  gracePeriodEndsAt: string | null;
  currentPeriodEnd: string | null;
  /** 해지 예약 (ISSUE_169 #5) — true 면 currentPeriodEnd 에 자동 해지 예정 */
  cancelAtPeriodEnd: boolean;
  /** 주기 변경 예약 (ISSUE_169 #22) — 다음 갱신부터 적용. null=예약 없음 */
  nextBillingCycle: BillingCycle | null;
}

/**
 * 구독 조회의 확신도 (ISSUE_195 D4).
 * `unknown`(미로드/조회실패)과 `loaded`(서버가 "구독 없음"이라고 확인)를 구분한다.
 * 예전엔 둘 다 `null` 이라 **결제 사용자도 부팅 직후·오프라인엔 무료로 강등**됐고,
 * 그 창에서 만든 세션이 유실됐다. 게이트는 여전히 fail-closed(모르면 잠금)지만,
 * "모름"을 알아야 재시도하고 사용자에게 경고할 수 있다.
 */
export type SubscriptionLoadState = "unknown" | "loading" | "loaded" | "error";

interface SubscriptionState {
  /** 현재 학원 구독. 학원 미선택/미로드 시 null. */
  academySubscription: Subscription | null;
  isLoading: boolean;
  /** ISSUE_195 D4: null 의 의미를 가르는 플래그 */
  loadState: SubscriptionLoadState;
}

interface SubscriptionActions {
  setAcademySubscription: (sub: Subscription | null) => void;
  setLoading: (loading: boolean) => void;
  /** ISSUE_195 D4: 조회 결과의 확신도 기록 */
  setLoadState: (state: SubscriptionLoadState) => void;
  clear: () => void;
}

type SubscriptionStore = SubscriptionState & SubscriptionActions;

// ============================================================================
// STORE
// ============================================================================

export const useSubscriptionStore = create<SubscriptionStore>()((set) => ({
  academySubscription: null,
  isLoading: false,
  loadState: "unknown",

  setAcademySubscription: (sub) => set({ academySubscription: sub, loadState: "loaded" }),
  setLoading: (isLoading) => set({ isLoading }),
  setLoadState: (loadState) => set({ loadState }),
  clear: () => set({ academySubscription: null, loadState: "unknown" }),
}));

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * 프리미엄 자격 판정 (단일 소스) — active(결제완료) + past_due(자동결제 실패 후 7일 유예 중).
 * 서버(subscription-enforce)가 유예 만료 시 past_due→canceled로 내려 자동 OFF. UI 'D-day 자동 재시도'·
 * 약관 '연체 확정되어도'와 정합(표준 dunning — 일시적 카드 실패로 즉시 끊지 않음). trialing(미결제 생성)·
 * canceled·null = 자격 없음. 위조 불가: subscriptions write는 service_role 전용(마이그 20260626190000).
 */
export function isPremiumActive(sub: Subscription | null): boolean {
  return sub?.status === "active" || sub?.status === "past_due";
}

/** 세션 다기기 동기화 가능 여부 = 프리미엄 자격(active + past_due 유예). 미결제/만료 = false (마스터데이터는 무료 저장). */
export function useSyncAllowed(): boolean {
  return useSubscriptionStore((state) => isPremiumActive(state.academySubscription));
}

/**
 * 결제 유도 필요 여부 — 학원 구독이 존재하나 active 가 아님(미결제 trialing / 연체 past_due / 만료 canceled).
 * 프리미엄(세션 다기기 동기화·realtime 협업·좌석) 유도 배너/CTA 노출 판단용. 미로드(null)면 false.
 * NB: past_due는 needsPayment=true(카드 갱신 유도)이면서 동시에 isPremiumActive=true(유예 중 프리미엄 유지) —
 * 의도된 조합(유예 동안 'D-day·재시도' 배너를 띄우되 기능은 끊지 않음).
 */
export function useNeedsPayment(): boolean {
  return useSubscriptionStore((state) => {
    const academy = state.academySubscription;
    return !!academy && academy.status !== "active";
  });
}

/** React 컴포넌트용: 현재 학원 구독 */
export function useAcademySubscription(): Subscription | null {
  return useSubscriptionStore((state) => state.academySubscription);
}

/** Non-React getter: sync 가능 여부 = 프리미엄 자격(active + past_due 유예). */
export function getSyncAllowed(): boolean {
  return isPremiumActive(useSubscriptionStore.getState().academySubscription);
}

/** Non-React getter: 결제 유도 필요 여부 */
export function getNeedsPayment(): boolean {
  const academy = useSubscriptionStore.getState().academySubscription;
  return !!academy && academy.status !== "active";
}
